/**
 * Tema claro / oscuro.
 *
 * Por omision se respeta la preferencia del sistema. Si la persona elige uno,
 * esa eleccion manda y se recuerda: el mostrador puede estar a contraluz aunque
 * el sistema operativo diga otra cosa.
 */

import { useCallback, useEffect, useState } from 'react';

export type Theme = 'light' | 'dark';

const KEY = 'abarrotes.theme';

function systemTheme(): Theme {
  return window.matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark';
}

function stored(): Theme | null {
  const value = localStorage.getItem(KEY);
  return value === 'light' || value === 'dark' ? value : null;
}

export function useTheme(): { theme: Theme; toggle: () => void } {
  const [theme, setTheme] = useState<Theme>(() => stored() ?? systemTheme());

  useEffect(() => {
    // Solo se marca el atributo cuando hay eleccion explicita; sin el, el CSS
    // sigue la preferencia del sistema por su cuenta.
    if (stored() === null) document.documentElement.removeAttribute('data-theme');
    else document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  useEffect(() => {
    if (stored() !== null) return;
    const media = window.matchMedia('(prefers-color-scheme: light)');
    const onChange = (): void => setTheme(systemTheme());
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, []);

  const toggle = useCallback(() => {
    setTheme((current) => {
      const next: Theme = current === 'dark' ? 'light' : 'dark';
      localStorage.setItem(KEY, next);
      document.documentElement.setAttribute('data-theme', next);
      return next;
    });
  }, []);

  return { theme, toggle };
}
